import { initializeApp } from "firebase/app";
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";

import { registerDeviceToken, unregisterDeviceToken } from "./notificationService";

/*
 * Firebase web push for the business portal.
 *
 * Everything here is optional: with no Firebase env, an unsupported browser or
 * a denied permission, push is simply off and the bell still works from the
 * API. Nothing in this file throws to its caller.
 */

const CONFIG = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// The token this browser last registered, so logout and a token change know
// what to remove. Not a secret from this user; it is their own device.
const TOKEN_STORAGE = "businessPushToken";
// Set once the permission prompt has been shown, so it is never shown again
// unprompted — the bell offers a button instead.
const PROMPTED_STORAGE = "businessPushPrompted";

// A scope of its own, so this worker never collides with any other.
const SW_SCOPE = "/firebase-cloud-messaging-push-scope";

let messagingPromise = null;

export const isPushConfigured = () =>
  Boolean(CONFIG.apiKey && CONFIG.projectId && CONFIG.messagingSenderId && CONFIG.appId && VAPID_KEY);

const messaging = () => {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      if (!isPushConfigured() || !("serviceWorker" in navigator) || !("Notification" in window)) {
        return null;
      }
      if (!(await isSupported())) return null;
      return getMessaging(initializeApp(CONFIG));
    })().catch(() => null);
  }
  return messagingPromise;
};

/**
 * "unsupported" | "default" | "granted" | "denied". Unsupported covers a
 * missing Firebase config too — to the user both mean "push is not available".
 */
export const pushPermission = async () => {
  if (!(await messaging())) return "unsupported";
  return Notification.permission;
};

const workerRegistration = () => {
  // The worker cannot read the build's env, so the public config rides on its URL.
  const url = `/firebase-messaging-sw.js?config=${encodeURIComponent(JSON.stringify(CONFIG))}`;
  return navigator.serviceWorker.register(url, { scope: SW_SCOPE });
};

/**
 * Get this browser's FCM token and register it with the API in `locale`.
 * Called after login, on every app load (which is how a refreshed token is
 * picked up), and on a language switch.
 *
 * With `prompt`, asks for permission if it has not been decided yet — but only
 * once unprompted; after that only an explicit click (`force`) asks again.
 */
export const syncPush = async (locale, { prompt = false, force = false } = {}) => {
  try {
    const m = await messaging();
    if (!m) return "unsupported";

    let permission = Notification.permission;
    const alreadyPrompted = localStorage.getItem(PROMPTED_STORAGE) === "1";

    if (permission === "default" && (force || (prompt && !alreadyPrompted))) {
      localStorage.setItem(PROMPTED_STORAGE, "1");
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") return permission;

    const token = await getToken(m, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: await workerRegistration(),
    });
    if (!token) return "unsupported";

    const previous = localStorage.getItem(TOKEN_STORAGE);
    if (previous && previous !== token) {
      await unregisterDeviceToken(previous).catch(() => {});
    }

    await registerDeviceToken(token, locale);
    localStorage.setItem(TOKEN_STORAGE, token);
    return "granted";
  } catch {
    return "unsupported";
  }
};

/** The token this browser registered, sent as `device_token` on logout. */
export const currentPushToken = () => localStorage.getItem(TOKEN_STORAGE);

/**
 * Remove this browser's token from the API — the fallback when logout itself
 * could not take it. Call before the JWT is dropped.
 */
export const unregisterPush = async () => {
  const token = localStorage.getItem(TOKEN_STORAGE);
  if (!token) return;
  localStorage.removeItem(TOKEN_STORAGE);
  await unregisterDeviceToken(token).catch(() => {});
};

/**
 * Forget this browser's push token locally and at FCM. After every sign-out,
 * and above all when the session ended without a logout call (refresh
 * refused, expired): the server still holds the token then, and only deleting
 * it at FCM stops the old account's pushes arriving here. The next sign-in
 * gets a fresh token.
 */
export const resetPush = async () => {
  localStorage.removeItem(TOKEN_STORAGE);
  try {
    const m = await messaging();
    if (!m || Notification.permission !== "granted") return;
    // deleteToken uses the worker getToken was given; with none it registers
    // Firebase's default worker over ours (same scope). getToken first, with
    // ours, returns the existing token and sets it.
    await getToken(m, { vapidKey: VAPID_KEY, serviceWorkerRegistration: await workerRegistration() });
    await deleteToken(m);
  } catch {
    // Push is optional; signing out never fails over it.
  }
};

/**
 * Foreground messages. While a tab has focus FCM hands the message here and
 * does NOT show an OS notification, so the in-app popup is the only one.
 * The handler gets the whole payload, `notification` and `data` both.
 * Returns an unsubscribe function.
 */
export const onForegroundPush = (handler) => {
  let unsubscribe = () => {};
  let cancelled = false;

  messaging().then((m) => {
    if (m && !cancelled) {
      unsubscribe = onMessage(m, handler);
    }
  });

  return () => {
    cancelled = true;
    unsubscribe();
  };
};
